// Full-time reveal sequencing. Pure computation extracted from
// GameweekGrid so the stagger/volley math is testable in isolation: given the
// set of newly-settled-and-unseen fixtures (group 3) with their result
// categories, produce a per-fixture entrance delay and the volley flag.
//
// The stagger index is computed WITHIN this batch only — already-seen or
// still-live fixtures never reach here, so they can't reserve a slot or leave
// a gap (the per-fixture trigger rewrite).

export type RevealCategory = 'miss' | 'partial' | 'outcome' | 'exact'

export interface RevealEntry {
  /** Milliseconds after the reveal starts that this card lands. */
  delay: number
  category: RevealCategory
  /** True only for exact scores when 3+ unseen exacts share this batch — they
   * slam together on a shared spotlight rather than each running a full solo
   * Phase A. */
  isVolley: boolean
}

export interface RevealPlan {
  entries: Record<string, RevealEntry>
  /** 3+ unseen exact scores in this batch. */
  volley: boolean
}

export interface RevealInput {
  gameId: string
  category: RevealCategory
}

// Reveal order and per-category stagger between siblings, matching the brief's
// "misses → partials → outcomes → exact" cascade with the miss/outcome
// intervals it specifies (130/170) and sensible in-between values for the
// other two.
const CATEGORY_ORDER: RevealCategory[] = ['miss', 'partial', 'outcome', 'exact']
const STAGGER_MS: Record<RevealCategory, number> = {
  miss: 130,
  partial: 150,
  outcome: 170,
  exact: 220,
}
// Breathing room inserted after each populated category so the next category's
// first card doesn't land on top of the previous category's last.
const CATEGORY_GAP_MS = 120
// When the volley cap trips, exacts cascade at this tighter interval instead
// of their solo 220ms spacing.
const VOLLEY_CASCADE_MS = 80
// 3+ unseen exact scores in a single view trips the shared-spotlight volley.
export const VOLLEY_THRESHOLD = 3
// How long into an exact fixture's own sequence the Phase A slam lands — the
// moment its reveal is marked seen. Also used for the
// volley's shared slam, measured from the first exact's delay.
export const PHASE_A_SLAM_LAND_MS = 1300
/** One shared period for every piece of idle debris, so the settled card puffs
 * once every several seconds instead of sputtering continuously. The flight
 * itself is unchanged — GameCard.css halved the active window when this
 * doubled, so the puff stays crisp and just comes round less often. */
export const IDLE_BURST_PERIOD_MS = 9000
/** Quiet beat before the settled card's first puff. Landing on the board and
 * being met with a burst in the same frame reads as drastic; the card should
 * settle in front of you and only then catch light. This is an
 * animation-delay, so it costs the first iteration only — the loop keeps its
 * period afterwards. */
export const IDLE_BURST_LEAD_IN_MS = 2000
/** How many of each kind the settled card loops, and how many the detonation
 * throws. The detonation counts are what the effects layer spawns per card.
 * The idle counts are deliberately far below a tenth of the burst: this is the
 * card's forever state, and at 22/12 it read as a firework, not an ember. */
export const BURST_SPARKS = 48
export const BURST_MOTES = 16
export const IDLE_SPARKS = 6
export const IDLE_MOTES = 3
/** Ambient specks on a settled exact card. */
export const DUST_COUNT = 16

/**
 * @param batch newly-settled-and-unseen fixtures, in display order.
 * @param reducedMotion when true, every card lands instantly (delay 0) and the
 *   volley never trips — there is no spectacle to pace.
 */
export function computeRevealPlan(
  batch: RevealInput[],
  reducedMotion: boolean
): RevealPlan {
  const exactCount = batch.filter((b) => b.category === 'exact').length
  const volley = !reducedMotion && exactCount >= VOLLEY_THRESHOLD

  const entries: Record<string, RevealEntry> = {}

  if (reducedMotion) {
    for (const item of batch) {
      entries[item.gameId] = { delay: 0, category: item.category, isVolley: false }
    }
    return { entries, volley: false }
  }

  let cursor = 0
  for (const category of CATEGORY_ORDER) {
    const items = batch.filter((b) => b.category === category)
    if (items.length === 0) continue

    const interval =
      category === 'exact' && volley ? VOLLEY_CASCADE_MS : STAGGER_MS[category]
    items.forEach((item, index) => {
      entries[item.gameId] = {
        delay: cursor + index * interval,
        category,
        isVolley: category === 'exact' && volley,
      }
    })
    cursor += items.length * interval + CATEGORY_GAP_MS
  }

  return { entries, volley }
}

export type DebrisKind = 'spark' | 'mote'

/** `burst` is the full-time detonation; `idle` is the quieter loop the settled
 * card runs for the rest of the season. Same vocabulary, a tenth of the volume. */
export type DebrisMode = 'burst' | 'idle'

export interface DebrisPiece {
  /** Throw distance in px from the burst centre. */
  tx: number
  ty: number
  /** Extra downward travel at the end, so pieces arc instead of flying flat. */
  gravity: number
  /** Diameter in px. */
  size: number
  /** Start offset (ms) inside the burst. */
  delay: number
  /** Flight time (ms). For `idle` this is the whole loop period — the flight
   * itself is compressed into the opening fraction of the keyframes. */
  duration: number
  /** Facing in degrees, so a spark streaks along its own direction of travel. */
  angle: number
  /** 1-4, selects one of the four particle colours for this kind. */
  colour: number
}

/** Fractional part of a large sine — a hash, not a PRNG. Deterministic per
 * index, so the scatter is stable across renders and nothing is random at
 * runtime, while looking nothing like the even ring an `i / count` sweep gives. */
function hash(index: number, salt: number): number {
  const x = Math.sin((index + 1) * 12.9898 + salt * 78.233) * 43758.5453
  return x - Math.floor(x)
}

/**
 * Deterministic debris for one burst.
 *
 * Depth comes from running two kinds at once: big soft `mote`s behind, small
 * fast `spark`s in front. Either on its own reads flat however it is tuned.
 * Within the sparks, a "far" half throws two to three times as wide as the
 * near half — a dense core plus a few long fliers is what reads as an
 * explosion, where one even spread reads as a machine.
 */
export function computeDebris(
  count: number,
  kind: DebrisKind,
  mode: DebrisMode = 'burst'
): DebrisPiece[] {
  const mote = kind === 'mote'
  const idle = mode === 'idle'
  return Array.from({ length: count }, (_, i) => {
    const heading = hash(i, 1) * Math.PI * 2
    const far = !mote && hash(i, 8) > (idle ? 0.55 : 0.5)
    const near = idle ? 38 : 55
    const spread = idle ? 52 : 110
    const distance = mote
      ? (idle ? 42 : 34) + hash(i, 2) * (idle ? 66 : 80)
      : (far ? (idle ? 82 : 170) : near) + hash(i, 2) * (far ? (idle ? 64 : 175) : spread)
    const tx = Math.cos(heading) * distance
    // A slight upward bias, so the burst throws rather than sprays flat.
    const ty = Math.sin(heading) * distance - 0.3 * distance
    return {
      tx: Math.round(tx),
      ty: Math.round(ty),
      gravity: Math.round((mote ? 4 : 16) + hash(i, 4) * (mote ? 20 : 74)),
      size: mote
        ? (idle ? 8 : 12) + Math.round(hash(i, 3) * (idle ? 11 : 16))
        : (far ? 3 : 4) + Math.round(hash(i, 3) * (far ? 2 : 5)),
      // Idle pieces carry the lead-in in their delay, so the whole puff waits
      // out the same quiet beat and only its stagger separates the pieces.
      delay:
        (idle ? IDLE_BURST_LEAD_IN_MS : 0) +
        Math.round(hash(i, 5) * (idle ? (mote ? 220 : 160) : mote ? 260 : 430)),
      duration: idle
        ? IDLE_BURST_PERIOD_MS
        : Math.round((mote ? 1600 : 820) + hash(i, 6) * (mote ? 1000 : 1050)),
      angle: Math.round((Math.atan2(ty, tx) * 180) / Math.PI),
      colour: Math.min(4, Math.floor(hash(i, 7) * 4) + 1),
    }
  })
}

export interface DustSpeck {
  /** Position as a percentage of a box slightly wider than the card, so some
   * specks drift past its edges instead of rising out of the bottom. */
  x: number
  y: number
  size: number
  opacity: number
  /** Drift, in px, over the speck's whole cycle. */
  tx: number
  ty: number
  duration: number
  delay: number
  /** 1-4, selects one of the four particle colours. */
  colour: number
}

/** The settled card's ambient dust: sparse, slow, and scattered across the
 * card rather than queued along its bottom edge. Hashed per index like the
 * debris, so it never re-scatters on re-render. */
export function computeDust(count: number): DustSpeck[] {
  const dustHash = (index: number, salt: number): number => {
    const x = Math.sin((index + 1) * 9.7531 + salt * 51.137) * 27183.845
    return x - Math.floor(x)
  }
  return Array.from({ length: count }, (_, i) => ({
    x: Math.round(-7 + dustHash(i, 1) * 114),
    y: Math.round(-12 + dustHash(i, 2) * 124),
    size: 2 + Math.round(dustHash(i, 3) * 3),
    opacity: Number((0.5 + dustHash(i, 5) * 0.45).toFixed(2)),
    tx: Math.round(-13 + dustHash(i, 6) * 26),
    ty: Math.round(-14 - dustHash(i, 7) * 26),
    duration: Math.round(7000 + dustHash(i, 8) * 6500),
    delay: Math.round(dustHash(i, 9) * 9000),
    colour: Math.min(4, Math.floor(dustHash(i, 4) * 4) + 1),
  }))
}

/** Result-category for a fixture from its base (pre-multiplier) points — the
 * same tiering GameCard uses (5+ exact, 3+ outcome, 1 partial, 0 miss). */
export function categoryForBasePoints(basePoints: number): RevealCategory {
  if (basePoints >= 5) return 'exact'
  if (basePoints >= 3) return 'outcome'
  if (basePoints >= 1) return 'partial'
  return 'miss'
}
